import { useMutation } from '@tanstack/react-query';
import { File as FileIcon, X, Download } from 'lucide-react-native';
import { remapProps } from 'nativewind';
import { useState } from 'react';
import { TextStyle, ViewStyle, TextProps as ReactNativeTextProps } from 'react-native';
import {
  Bubble,
  BubbleProps,
  Day,
  IMessage,
  Message,
  MessageProps,
  MessageText,
  MessageTextProps,
} from 'react-native-gifted-chat';
import { twJoin } from 'tailwind-merge';

import {
  getMessageReference,
  messageAttributesSchema,
  shouldRenderReferenceForViewer,
  type MessageAttributes,
  type ProcessingStatus,
} from '../chatHelpers';
import { ConfirmModal } from '../confirmModal';
import { Center } from '../ui/center';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useToaster } from '@/hooks/useToaster';
import type { OutboxStatus } from '@/services/chat/outboxStore';
import { createDownloadUrlV2 } from '@/services/createDownloadUrlV2';
import { deleteAttachmentV2 } from '@/services/deleteAttachmentV2';
import type { ClinicalCase } from '@/types/clinicalCase';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { downloadFile } from '@/util/downloadFile';
import { getNativeTextProps } from '@/util/getNativeTextProps';
import { Sentry } from '@/util/sentry';
import { isSameUserId } from '@/utils/isSameUserId';

export {
  getMessageReference,
  isMessageFromCurrentUser,
  messageAttributesSchema,
  shouldRenderReferenceForViewer,
} from '../chatHelpers';

export type { ProcessingStatus } from '../chatHelpers';

/**
 * A parsed chat message: GiftedChat's runtime shape plus every field the
 * message-attribute catalog can produce.
 *
 * Only fields with no attribute equivalent are declared here. Everything else
 * arrives from `MessageAttributes`, so adding a field to a message type's
 * module surfaces it here automatically instead of needing a hand-mirrored
 * copy that can drift.
 *
 * The `pipelineStatus` omission is the one deliberate carve-out: that wire key
 * is renamed to `processingStatus` on parse, so exposing both would let a
 * caller read the raw key that no parsed message ever carries.
 */
export interface ICustomMessage
  extends IMessage, Omit<MessageAttributes, 'pipelineStatus' | 'pipelineStatusAt'> {
  messageId: string;
  isAutoReference?: boolean;
  /** Backend-driven async processing status; absent when the producer
   * is not configured or the message type doesn't go through async
   * processing. Wire key on Twilio attributes is `pipelineStatus`; we
   * surface it as the more generic `processingStatus`. */
  processingStatus?: ProcessingStatus;
  /** ISO-8601 timestamp the most recent status transition was written. */
  processingStatusAt?: string;
  /** Set on the opening message only, from the inquiry: the submitter's case, shown in its bubble. */
  clinicalCase?: ClinicalCase;
  /**
   * Set only on the reader's own message that is not stored yet: where its
   * send stands. Such a message has no sid, so nothing that acts on a stored
   * message may act on it.
   */
  outboxStatus?: OutboxStatus;
}

export type CustomBubbleProps = BubbleProps<ICustomMessage> & {
  leftBottomContainerStyle: ViewStyle;
  rightBottomContainerStyle: ViewStyle;
  leftWrapperStyle: ViewStyle;
  rightWrapperStyle: ViewStyle;
};

function CustomBubble({
  leftBottomContainerStyle,
  rightBottomContainerStyle,
  tickStyle,
  ...props
}: CustomBubbleProps) {
  return (
    <Bubble
      {...props}
      bottomContainerStyle={{
        left: leftBottomContainerStyle,
        right: rightBottomContainerStyle,
      }}
      tickStyle={tickStyle}
    />
  );
}

export const StyledBubble = remapProps(CustomBubble, {
  leftBottomContainerClassName: 'leftBottomContainerStyle',
  rightBottomContainerClassName: 'rightBottomContainerStyle',
  tickClassName: 'tickStyle',
});

export const StyledDay = remapProps(Day, {
  containerClassName: 'containerStyle',
  textClassName: 'textStyle',
});

export type CustomMessageTextProps = MessageTextProps<IMessage> & {
  rightMessageContainerStyle: ViewStyle;
  leftMessageContainerStyle: ViewStyle;
  leftTextStyle: TextStyle;
  rightTextStyle: TextStyle;
  customMessageTextStyle: TextStyle;
  textProps?: ReactNativeTextProps;
};

function CustomMessageText({
  rightMessageContainerStyle,
  leftMessageContainerStyle,
  customMessageTextStyle,
  textProps,
  ...props
}: CustomMessageTextProps) {
  return (
    <MessageText
      {...props}
      textProps={{ ...textProps, testID: 'message-text' }}
      containerStyle={{
        right: rightMessageContainerStyle,
        left: leftMessageContainerStyle,
      }}
      customTextStyle={customMessageTextStyle}
    />
  );
}

export const StyledMessageText = remapProps(CustomMessageText, {
  rightContainerClassName: 'rightMessageContainerStyle',
  leftContainerClassName: 'leftMessageContainerStyle',
  customTextClassName: 'customMessageTextStyle',
});

export type CustomMessageProps = MessageProps<IMessage> & {
  rightContainerStyle: ViewStyle;
  leftContainerStyle: ViewStyle;
};

const CustomMessage = ({
  rightContainerStyle,
  leftContainerStyle,
  ...props
}: CustomMessageProps) => {
  return (
    <Message
      {...props}
      containerStyle={{
        left: leftContainerStyle,
        right: rightContainerStyle,
      }}
    />
  );
};

export const StyledCustomMessage = remapProps(CustomMessage, {
  rightContainerClassName: 'rightContainerStyle',
  leftContainerClassName: 'leftContainerStyle',
});

export function FileItem({
  disabled = false,
  fileName,
  onDelete,
  deleteDisabled = false,
  children,
  position = 'right',
}: {
  disabled?: boolean;
  fileName: string;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  children?: React.ReactNode;
  position?: 'left' | 'right';
}) {
  return (
    <HStack
      className={twJoin(
        'rounded-md p-2',
        disabled ? 'opacity-50' : '',
        position === 'right' ? 'bg-background-50' : 'bg-background-0'
      )}>
      <HStack className="flex-1 items-center gap-x-6">
        <Center
          className={twJoin(
            'rounded-md p-2',
            position === 'left' ? 'bg-background-50' : 'bg-background-0'
          )}>
          <Icon as={FileIcon} size="md" className="text-typography-900" />
        </Center>

        {children}

        <Text
          isTruncated
          {...getNativeTextProps()}
          className="truncated flex-1 font-semibold text-sm text-typography-900">
          {fileName}
        </Text>

        <Icon as={Download} size="sm" className="text-typography-600" />
      </HStack>

      {onDelete ? (
        <VStack className="ml-4 justify-center pr-2">
          <Pressable
            disabled={deleteDisabled}
            onPress={onDelete}
            {...getAccessibilityProps({ role: 'button', label: `Remove attachment ${fileName}` })}
            hitSlop={{
              top: 16,
              bottom: 16,
              left: 16,
              right: 16,
            }}
            className="rounded-sm bg-transparent p-1.5 hover:bg-background-100 active:bg-background-200 disabled:opacity-50">
            <Icon as={X} size="md" className="text-typography-600" />
          </Pressable>
        </VStack>
      ) : null}
    </HStack>
  );
}

/**
 * Chat Custom View Component for File Attachments
 *
 * @overview
 * Renders file attachments within chat message bubbles. Handles file downloads,
 * deletion permissions, and user interactions for both experts and LMP users.
 *
 * @behavior
 * - Displays files with download functionality
 * - Shows delete button only for message authors in active conversations
 * - Handles file download with progress feedback
 * - Manages deletion confirmations and error states
 * - Tracks analytics for all file interactions
 *
 * @permissions
 * - Download: Available to all conversation participants
 * - Delete: Only available to message author when conversation is active
 */
export function ChatCustomView({
  currentMessage,
  position,
  isAuthor,
  conversationId,
  closedConversation,
  userId,
}: CustomBubbleProps & {
  isAuthor: boolean;
  conversationId: string;
  closedConversation: boolean;
  userId: string;
}) {
  const { track } = useChatAnalytics();

  const toaster = useToaster();

  const { mutateAsync: createDownloadUrl, isPending: isCreatingDownloadUrl } = useMutation({
    mutationFn: createDownloadUrlV2,
  });

  const { mutateAsync: deleteAttachment, isPending: isDeletingAttachment } = useMutation({
    mutationFn: deleteAttachmentV2,
    onSuccess: (_, variables) => {
      toaster.showSuccess({
        title: `File deleted`,
      });

      const file = currentMessage?.files?.find((f) => f.s3Key === variables.s3Key);

      track(ANALYTICS_EVENTS.REMOVE_ATTACHMENT_FILE_COMPLETED, {
        file_name: file?.fileName,
        s3_key: file?.s3Key,
        message_sid: currentMessage?.messageId,
        message_id: currentMessage?.messageId,
      });

      setFileToDelete(undefined);
    },
    onError: (e, variables) => {
      console.error(e);

      const file = currentMessage?.files?.find((f) => f.s3Key === variables.s3Key);

      track(ANALYTICS_EVENTS.REMOVE_ATTACHMENT_FILE_FAILED, {
        file_name: file?.fileName,
        s3_key: file?.s3Key,
        message_sid: currentMessage?.messageId,
        message_id: currentMessage?.messageId,
        error: e instanceof Error ? e.message : JSON.stringify(e),
      });

      Sentry.captureException(e);

      toaster.showError({
        title: `Failed to delete file`,
      });

      setFileToDelete(undefined);
    },
  });

  const [fileToDelete, setFileToDelete] = useState<{ fileName: string; s3Key: string } | undefined>(
    undefined
  );

  if (!currentMessage?.files || currentMessage.files.length === 0) {
    return null;
  }

  const isMessageAuthor = currentMessage?.user._id === userId;

  return (
    <HStack
      className={twJoin(
        '-my-1 w-full flex-wrap items-center gap-2 border-0 px-3 py-2',
        position === 'left'
          ? 'bg-background-50 web:justify-start'
          : 'bg-primary-500 web:justify-end'
      )}>
      <ConfirmModal
        isOpen={!!fileToDelete}
        title="Are you sure you want to delete this file?"
        description="Deleting this file will remove it from the chat, but will not affect any already downloaded files. This action cannot be undone."
        acceptText="Delete"
        acceptAction="negative"
        actionInProgress={isDeletingAttachment}
        onConfirm={() => {
          if (!fileToDelete) {
            return;
          }

          deleteAttachment({
            s3Key: fileToDelete.s3Key,
            conversationId,
            messageId: currentMessage?.messageId ?? '',
          });
        }}
        onCancel={() => {
          if (!fileToDelete) {
            return;
          }

          track(ANALYTICS_EVENTS.REMOVE_ATTACHMENT_FILE_CANCELED, {
            file_name: fileToDelete.fileName,
            s3_key: fileToDelete.s3Key,
            message_sid: currentMessage?.messageId,
            message_id: currentMessage?.messageId,
          });

          setFileToDelete(undefined);
        }}
      />

      {currentMessage?.files?.map((file) => (
        <Pressable
          disabled={isCreatingDownloadUrl}
          className="w-full min-w-[300px] max-w-full md:max-w-[300px]"
          onPress={async () => {
            const closeInfoToast = toaster.showInfo({
              title: `Downloading file: ${file.fileName}`,
            });

            track(ANALYTICS_EVENTS.ATTACHMENT_DOWNLOAD_STARTED, {
              file_name: file.fileName,
              s3_key: file.s3Key,
              message_sid: currentMessage?.messageId,
              message_id: currentMessage?.messageId,
              is_author: isAuthor,
            });

            try {
              const data = await createDownloadUrl(file.s3Key);

              if (!data?.url) {
                closeInfoToast();

                toaster.showError({
                  title: `Failed to download file: ${file.fileName}`,
                });

                return;
              }

              await downloadFile({ url: data.url, fileName: file.fileName });

              closeInfoToast();

              track(ANALYTICS_EVENTS.ATTACHMENT_DOWNLOAD_COMPLETED, {
                file_name: file.fileName,
                s3_key: file.s3Key,
                message_sid: currentMessage?.messageId,
                message_id: currentMessage?.messageId,
                is_author: isAuthor,
              });

              toaster.showSuccess({
                title: `Downloaded file: ${file.fileName}`,
              });
            } catch (error) {
              console.error(error);

              Sentry.captureException(error, {
                tags: {
                  component: 'ChatAttachment',
                  source: 'attachment-download',
                },
                extra: {
                  errorMessage: error instanceof Error ? error.message : String(error),
                  fileName: file.fileName,
                  s3Key: file.s3Key,
                  conversationId,
                  messageId: currentMessage?.messageId,
                },
              });

              closeInfoToast();

              track(ANALYTICS_EVENTS.ATTACHMENT_DOWNLOAD_FAILED, {
                file_name: file.fileName,
                s3_key: file.s3Key,
                message_sid: currentMessage?.messageId,
                message_id: currentMessage?.messageId,
                is_author: isAuthor,
                error: error instanceof Error ? error.message : JSON.stringify(error),
              });

              toaster.showError({
                title: `Failed to download file: ${file.fileName}`,
              });
            }
          }}
          key={file.s3Key}>
          <FileItem
            position={position}
            fileName={file.fileName}
            deleteDisabled={isDeletingAttachment}
            onDelete={
              !isMessageAuthor || closedConversation
                ? undefined
                : () => {
                    track(ANALYTICS_EVENTS.REMOVE_ATTACHMENT_FILE_STARTED, {
                      file_name: file.fileName,
                      s3_key: file.s3Key,
                      message_sid: currentMessage?.messageId,
                      message_id: currentMessage?.messageId,
                    });

                    setFileToDelete(file);
                  }
            }
          />
        </Pressable>
      ))}
    </HStack>
  );
}
